import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { IdPathBuilder, Raw_Id_Path_Head } from "../navigation/IdPathBuilder";
import { identity } from "../validation";
import DestructorTree from "../DestructorTree";
import { evaluateStyle, hasStyle, initializedStyle, unsetStyle } from "../state 2 interface/style";
import {
	evaluateNumericalTypeProperty,
	initializedNumericalTypeProperties,
} from "../state 2 interface/numericalTypeProperties";
import createIdPathBuilderFromClientIdPath from "../navigation/createIdPathBuilderFromClientIdPath";
import { getDecimalsFromNumericalTypeProperties, serializeNumber } from "../state 2 interface/serializeNumber";
import uuid = require("../state/uuid");
import walkNodePath from "../navigation/walkNodePath";
import { client_state } from "../state/ClientState";
import * as Collection from "./Collection";
import * as Entry from "./Entry";
import { iReference } from "./Reference";
import subscribeToClientQueryEntry from "./subscribeToClientQueryEntry";
import { patchQuery } from "./sorting";
import { Cunknown__initialized } from "../../../../../common/api_configuration-state/lib/types/client_state/alan_api";
import createClientIdPathFromNode from "../navigation/createClientIdPathFromNode";
import {
	registerIdPath,
	registerNodeContext,
	registerStategroup,
	registerCollection,
} from "../state/registerClientState";
import { addChild } from "../DestructorTree";
import { getTranslation } from "./phrases";
import { linkIdPath } from "./ClientIdPath";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { isResolved, Unknown } from "../Status";
import ClientStateObjectProxy from "./ClientStateObjectProxy";
import { Scalar, WeakCache, createExpressionBuilder, createVariable } from "../../../../../stream";
import { iCollection } from "../state/Collection";

type PatchedQuery = { subscribed?: boolean };

export function setSubscribed(query: api_cs.Cquery__client_state): DestructorTree | undefined {
	const patched_query = query as unknown as PatchedQuery;
	if (patched_query.subscribed) {
		return;
	} else {
		patched_query.subscribed = true;
		const destructor = addChild(query);
		destructor.onDestroy(() => {
			patched_query.subscribed = false;
		});
		return destructor;
	}
}

export function isSubscribed(query: api_cs.Cquery__client_state): boolean {
	const patched_query = query as unknown as PatchedQuery;
	return patched_query.subscribed !== undefined && patched_query.subscribed;
}

export interface QueryEntryLocation {
	<T>(local: (entry: api_cs.Centries__collection) => T, remote: (id_path_builder: IdPathBuilder) => T): T;
}

export function localQueryEntry(entry: api_cs.Centries__collection): QueryEntryLocation {
	return (context_callback) => context_callback(entry);
}

export function remoteQueryEntry(id_path_builder: IdPathBuilder): QueryEntryLocation {
	return (_, context_callback) => context_callback(id_path_builder);
}

export function hasResult(query: api_cs.Cquery__client_state): api_cs.Cyes__has_result {
	switch (query.properties.has_result.state.name) {
		case "no":
			query.root.transact(() => {
				query.properties.has_result.set("yes", {
					"selected entries": "none",
					"amount of entries visible": 0,
					"is empty": ["yes", {}],
					"total amount of entries": 0,
					"backend updates are available": ["no", {}],
					"has more entries than maximum": ["no", {}],
					entries: {},
				});
				patchQuery(query.properties.has_result.cast("yes"));
			});
			break;
		case "yes":
			break;
		default:
			unexpectedState(query.properties.has_result.state);
	}
	return query.properties.has_result.cast("yes");
}

export function updateIsEmpty(query: api_cs.Cyes__has_result): void {
	const amount_of_entries_visible = query.properties.amount_of_entries_visible.value;
	if (amount_of_entries_visible === 0) {
		switch (query.properties.is_empty.state.name) {
			case "no": {
				query.properties.is_empty.set("yes", {});
				break;
			}
			case "yes":
				break;
			default:
				unexpectedState(query.properties.is_empty.state);
		}
	} else {
		switch (query.properties.is_empty.state.name) {
			case "no": {
				const not_empty = query.properties.is_empty.state.node;
				switch (not_empty.properties.result_size.state.name) {
					case "multiple": {
						if (amount_of_entries_visible === 1) {
							not_empty.properties.result_size.set("one", {});
						}
						break;
					}
					case "one": {
						if (amount_of_entries_visible > 1) {
							not_empty.properties.result_size.set("multiple", {});
						}
						break;
					}
					default:
						unexpectedState(not_empty.properties.result_size.state);
				}
				break;
			}
			case "yes": {
				query.properties.is_empty.set("no", {
					"result size": identity<api_cs.Tno__is_empty__yes__has_result["result size"]>(
						amount_of_entries_visible === 1 ? ["one", {}] : ["multiple", {}]
					),
				});
				break;
			}
			default:
				unexpectedState(query.properties.is_empty.state);
		}
	}
}

function compareIds(id_path: Raw_Id_Path_Head, id: api_cs.Cid_path): boolean {
	return id.properties.has_steps.switch({
		no: () => id_path["has steps"][0] === "no",
		yes: ($) => {
			if (id_path["has steps"][0] === "no") {
				return false;
			}
			const path_type = id_path["has steps"][1].type;
			const type_result = $.properties.type.switch({
				"collection entry": ($) =>
					path_type[0] === "collection entry" &&
					path_type[1].collection === $.properties.collection.entry &&
					path_type[1].id === $.properties.id,
				group: ($) => path_type[0] === "group" && path_type[1].group === $.properties.group.entry,
				state: ($) =>
					path_type[0] === "state" &&
					path_type[1]["state group"] === $.properties.state_group.entry &&
					path_type[1]["state"] === $.properties.state.entry,
			});
			return type_result && compareIds(id_path["has steps"][1].tail, $.properties.tail);
		},
	});
}

export function isEntrySelected(
	query: api_cs.Cquery__client_state,
	entry_location: QueryEntryLocation
): api_cs.Tentries__yes__has_result["is selected"] {
	switch (query.properties.has_selected_entries.state.name) {
		case "no":
			return "no";
		case "yes": {
			const $ = query.properties.has_selected_entries.state.node;
			return entry_location(
				(entry) => {
					for (const [key, selected_entry] of $.properties.selected_entries) {
						switch (selected_entry.properties.entry_status.properties.initialized.state.name) {
							case "unknown":
								break;
							case "yes": {
								const local = selected_entry.properties.entry_status.properties.initialized.state.node;
								if (local.properties.referenced_node.output.result_node().is(entry.properties.node)) {
									return ["yes", { "selected entry": key }];
								}
								break;
							}
							case "no":
								break;
							default:
								unexpectedState(selected_entry.properties.entry_status.properties.initialized.state);
						}
					}
					return "no";
				},
				(id_path_builder) => {
					const id_path = id_path_builder.build();
					for (const [key, entry] of $.properties.selected_entries) {
						switch (entry.properties.entry_status.properties.initialized.state.name) {
							case "unknown":
								break;
							case "yes": {
								const local = entry.properties.entry_status.properties.initialized.state.node;
								const selected_entry_path = createIdPathBuilderFromNode(
									local.properties.referenced_node.output.result_node()
								);
								if (selected_entry_path.equals(id_path_builder)) {
									return ["yes", { "selected entry": key }];
								}
								break;
							}
							case "no": {
								const remote = entry.properties.entry_status.properties.initialized.state.node;
								if (compareIds(id_path, remote.properties.referenced_node)) {
									return ["yes", { "selected entry": key }];
								}
								break;
							}
							default:
								unexpectedState(entry.properties.entry_status.properties.initialized.state);
						}
					}
					return "no";
				}
			);
		}
		default:
			unexpectedState(query.properties.has_selected_entries.state);
	}
}

export function subscribeToEntryStyle(entry: api_cs.Centries__yes__has_result, destructor: DestructorTree): void {
	const query = entry.parent.parent;
	query.properties.definition.ref.properties.context.state.node.output
		.selected_context()
		.input.application_node()
		.input.location()
		.switch({
			group: undefined,
			state: undefined,
			collection: ($) => {
				switch ($.properties.ui.properties.has_style.state.name) {
					case "no":
						break;
					case "yes": {
						const has_entry_style = $.properties.ui.properties.has_style.state.node;
						const context_node = entry.properties.entry_status.properties.initialized.switch({
							unknown: () => null,
							yes: ($) => $.properties.referenced_node.output.result_node(),
							no: ($) => registerIdPath(createIdPathBuilderFromClientIdPath($.properties.referenced_node), destructor),
						});
						if (context_node !== null) {
							evaluateStyle(
								initializedStyle(entry.properties.style),
								has_entry_style.properties.style_expression,
								context_node
							);
						}
						break;
					}
					default:
						unexpectedState($.properties.ui.properties.has_style.state);
				}
			},
			root: undefined,
		});
}

export function subscribeToCellNumericalTypeProperties(
	number_cell: api_cs.Cnumber__type__cells,
	this_node: api_cs.Cnode,
	destructor: DestructorTree
): void {
	const number_column = number_cell.inferences.column_type();
	const app_number = number_column.inferences.number_type().properties.number.ref.inferences.number();
	const gui_numerical_type = number_column.inferences.number_type().properties.number.ref.properties.numerical_type.ref;
	const numerical_type_properties = initializedNumericalTypeProperties(
		number_cell.properties.numerical_type_properties
	);
	const subscription = numerical_type_properties.subscribe_decimals({
		on_update: () => {
			switch (number_cell.properties.is_set.state.name) {
				case "no":
					break;
				case "yes": {
					const $ = number_cell.properties.is_set.state.node;
					$.set_textual_representation(
						serializeNumber(
							$.properties.number.value,
							getDecimalsFromNumericalTypeProperties(numerical_type_properties.parent),
							app_number.properties.numerical_type.ref
						)
					);
					break;
				}
				default:
					unexpectedState(number_cell.properties.is_set.state);
			}
		},
	});
	destructor.onDestroy(() => {
		subscription.unsubscribe();
	});
	evaluateNumericalTypeProperty(numerical_type_properties, app_number, this_node);
	switch (app_number.properties.ui.properties.dynamic_numerical_type.state.name) {
		case "no":
			switch (gui_numerical_type.key.ref.properties.ui.properties.represent_as.state.name) {
				case "model":
					break;
				case "date":
					numerical_type_properties.set_unit("");
					break;
				case "date and time":
					numerical_type_properties.set_unit("");
					break;
				case "duration":
					numerical_type_properties.set_unit("");
					break;
				case "custom type":
					break;
				default:
					unexpectedState(gui_numerical_type.key.ref.properties.ui.properties.represent_as.state);
			}
			break;
		case "yes":
			break;
		default:
			unexpectedState(app_number.properties.ui.properties.dynamic_numerical_type.state);
	}
}

export function subscribeToStategroupCellStyle(
	stategroup_cell: api_cs.Cstate_group__type__cells,
	parent_context: api_cs.Cstate_group__type__property | IdPathBuilder,
	destructor: DestructorTree
): void {
	const state_group_column = stategroup_cell.inferences.column_type();
	const app_stategroup = state_group_column.inferences
		.state_group_type()
		.properties.state_group.ref.inferences.state_group();
	const stategroup_name = app_stategroup.parent.parent.key;
	if (hasStyle(app_stategroup)) {
		destructor.subscribeToStategroup(stategroup_cell.properties.is_set, (destructor) => {
			switch (stategroup_cell.properties.is_set.state.name) {
				case "no":
					break;
				case "yes": {
					const is_set = stategroup_cell.properties.is_set.state.node;
					destructor.subscribeToText(is_set, "subscribe_state", (state_name, destructor) => {
						const style = initializedStyle(is_set.properties.style);
						const app_state = app_stategroup.properties.states.get(state_name);
						if (app_state) {
							unsetStyle(style);
							switch (app_state.properties.ui.properties.has_style.state.name) {
								case "no":
									break;
								case "yes": {
									const $ = app_state.properties.ui.properties.has_style.state.node;
									let context_node: api_cs.Cnode;
									if (parent_context instanceof api_cs.Cstate_group__type__property) {
										context_node = parent_context.properties.states.get(state_name)!.properties.node;
									} else {
										context_node = registerIdPath(parent_context.state(stategroup_name, state_name), destructor);
									}
									evaluateStyle(style, $.properties.style_expression, context_node);
									break;
								}
								default:
									unexpectedState(app_state.properties.ui.properties.has_style.state);
							}
						}
					});
					break;
				}
			}
		});
	}
}

function subscribeToEntryExpressions(target: api_cs.Centries__yes__has_result, source: api_cs.Centries__collection) {
	const destructor = addChild(target);

	subscribeToEntryStyle(target, destructor);

	target.properties.cells.forEach((cell) => {
		switch (cell.properties.type.state.name) {
			case "number": {
				const number_cell = cell.properties.type.state.node;
				const number_column = number_cell.inferences.column_type();
				const app_number = number_column.inferences.number_type().properties.number.ref.inferences.number();
				switch (app_number.properties.ui.properties.dynamic_numerical_type.state.name) {
					case "no": {
						const this_node = client_state.properties.root;
						subscribeToCellNumericalTypeProperties(number_cell, this_node, destructor);
						break;
					}
					case "yes": {
						const node = walkNodePath(
							number_column.inferences.number_type().parent.properties.path,
							source.properties.node
						);
						if (node) {
							registerNodeContext(node, destructor);
							subscribeToCellNumericalTypeProperties(number_cell, node, destructor);
						}
						break;
					}
					default:
						unexpectedState(app_number.properties.ui.properties.dynamic_numerical_type.state);
				}
				break;
			}
			case "file":
				break;
			case "text":
				break;
			case "widget":
				break;
			case "state group": {
				const state_group_cell = cell.properties.type.state.node;
				const state_group_column = state_group_cell.inferences.column_type().inferences.state_group_type();
				const node = walkNodePath(state_group_column.parent.properties.path, source.properties.node);
				if (node) {
					registerNodeContext(node, destructor);
					const state_group_definition = state_group_column.properties.state_group.ref.inferences.state_group();
					const state_group = registerStategroup(state_group_definition, node, destructor);
					subscribeToStategroupCellStyle(state_group_cell, state_group, destructor);
				}
				break;
			}
		}
	});
}

export function getTotalAmountOfLocalEntries(query: api_cs.Cquery__client_state): number {
	switch (query.properties.entries_are_editable.state.name) {
		case "yes": {
			const editable = query.properties.entries_are_editable.state.node;
			switch (editable.properties.can_create_entry.state.name) {
				case "yes": {
					const can_create_entry = editable.properties.can_create_entry.state.node;
					switch (can_create_entry.properties.in_.state.name) {
						case "query": {
							const editable_query = can_create_entry.properties.in_.state.node;
							const collection = editable_query.properties.collection.ref;
							return Collection.getAmountOfLocalEntries(collection);
						}
						case "candidates":
							break;
					}
					break;
				}
				case "no":
					break;
			}
			break;
		}
		case "no":
			break;
	}
	switch (query.properties.use_for.state.name) {
		case "query": //already handled above
			break;
		case "candidates": {
			const reference = query.properties.use_for.state.node.inferences.reference_property().parent;
			const collection = iReference(reference).collectionContext.value;
			if (isResolved(collection)) {
				return Collection.getAmountOfLocalEntries(collection);
			}
			break;
		}
	}
	return 0;
}

export function removeResultEntry(entry: api_cs.Centries__yes__has_result): void {
	const has_result = entry.parent;
	const new_amount_of_entries = has_result.properties.amount_of_entries_visible.value - 1;
	has_result.set_amount_of_entries_visible(new_amount_of_entries);
	has_result.properties.entries.delete(entry.key);
	updateIsEmpty(has_result);
}

export function subscribeToCollectionEntry(entry_status: api_cs.Cquery_entry_status): void {
	const destructor = addChild(entry_status);
	const position = entry_status.input.position();
	switch (position.variant.name) {
		case "result entry": {
			const entry = position.variant.definition;
			const query = entry.parent.parent;
			const can_create_entry = query.properties.entries_are_editable.switch({
				yes: ($) =>
					$.properties.can_create_entry.switch({
						yes: true,
						no: false,
					}),
				no: false,
			});
			if (!can_create_entry) {
				subscribeToClientQueryEntry(entry, destructor);
			} else {
				destructor.subscribeToStategroup(entry_status.properties.initialized, (destructor) => {
					switch (entry_status.properties.initialized.state.name) {
						case "yes": {
							const initialized = entry_status.properties.initialized.state.node;
							const collection_entry = initialized.properties.referenced_node.output
								.result_node()
								.input.entity()
								.cast("entry");
							destructor.subscribeToStategroup(collection_entry.properties.node.properties.is_valid, () => {
								const collection_entry_status = collection_entry.properties.node.properties.is_valid.state.name;
								const result_entry_status = entry.properties.is_valid.state.name;
								if (result_entry_status !== collection_entry_status) {
									entry.properties.is_valid.set(collection_entry_status, {});
								}
							});
							destructor.subscribeToStategroup(collection_entry.properties.node.properties.can_be_deleted, () => {
								const collection_entry_status = collection_entry.properties.node.properties.can_be_deleted.state.name;
								const result_entry_status = entry.properties.is_editable.state.name;
								if (result_entry_status !== collection_entry_status) {
									entry.properties.is_editable.set(collection_entry_status, {});
								}
							});
							subscribeToEntryExpressions(entry, collection_entry);
							subscribeToClientQueryEntry(entry, destructor);
							break;
						}
						case "no":
							subscribeToClientQueryEntry(entry, destructor);
							break;
						case "unknown":
							break;
						default:
							unexpectedState(entry_status.properties.initialized.state);
					}
				});
			}
			break;
		}
		case "selected entry":
			break;
		default:
			unexpectedState(position.variant);
	}
	destructor.subscribeToStategroup(entry_status.properties.initialized, () => {
		switch (entry_status.properties.initialized.state.name) {
			case "yes": {
				const initialized = entry_status.properties.initialized.state.node;
				linkIdPath(initialized.properties.referenced_node);
				break;
			}
			case "no":
				break;
			case "unknown":
				break;
			default:
				unexpectedState(entry_status.properties.initialized.state);
		}
	});
}

function createResultEntry(
	query: api_cs.Cquery__client_state,
	entry_location: QueryEntryLocation
): api_cs.Tentries__yes__has_result {
	return {
		"is editable": entry_location(
			(entry) => {
				switch (entry.properties.location.state.name) {
					case "local":
						return ["yes", {}];
					case "remote":
						return ["no", {}];
				}
			},
			() => ["no", {}]
		),
		"use for": query.properties.use_for.switch<api_cs.Tentries__yes__has_result["use for"]>({
			candidates: ["candidates", {}],
			query: ["query", {}],
		}),
		"is valid": ["yes", {}],
		"entry status": entry_location<api_cs.Tquery_entry_status>(
			(entry) => ({
				initialized: [
					"yes",
					{
						"referenced node": createClientIdPathFromNode(entry.properties.node),
					},
				],
			}),
			(id_path) => ({
				initialized: [
					"no",
					{
						"referenced node": id_path.build(),
					},
				],
			})
		),
		style: { initialized: "no" },
		"is selected": isEntrySelected(query, entry_location),
		"has cells": query.properties.has_columns.switch<api_cs.Tentries__yes__has_result["has cells"]>({
			no: ["no", {}],
			yes: ($) => ["yes", { first: $.properties.first.entry }],
		}),
		cells: query.properties.columns.map(($) => ({
			"has successor": $.properties.has_successor.switch<api_cs.Tcells["has successor"]>({
				no: ["no", {}],
				yes: ($) => ["yes", { successor: $.properties.successor.entry }],
			}),
			"column type": $.properties.column_type.switch({
				id: ["id", {}],
				content: ["content", {}],
			}),
			type: $.properties.type.switch({
				text: [
					"text",
					{
						"is set": ["no", {}],
					},
				],
				file: [
					"file",
					{
						"is set": ["no", {}],
					},
				],
				number: [
					"number",
					{
						"is set": ["no", {}],
						"numerical type properties": {
							initialized: "no",
						},
					},
				],
				"state group": ($): api_cs.Tcells["type"] => [
					"state group",
					{
						"is set": ["no", {}],
						states: $.properties.has_filter.switch({
							no: () => ({}),
							yes: ($) =>
								$.properties.states.map<api_cs.Tstates__state_group__type__cells>(($) => ({
									name: getTranslation($.key_value),
									"is selected": ["no", {}],
								})),
						}),
					},
				],
				widget: [
					"widget",
					{
						initialized: ["no", {}],
					},
				],
			}),
		})),
	};
}

export function addResultEntry(
	has_result: api_cs.Cyes__has_result,
	entry_location: QueryEntryLocation
): api_cs.Centries__yes__has_result {
	const internal_key = uuid.generate();
	const entry = has_result.properties.entries.create(
		internal_key,
		createResultEntry(has_result.parent, entry_location)
	);

	const amount_of_entries = has_result.properties.amount_of_entries_visible.value + 1;
	has_result.set_amount_of_entries_visible(amount_of_entries);
	updateIsEmpty(has_result);

	subscribeToCollectionEntry(entry.properties.entry_status);
	return entry;
}

function getKeyFromIdPath(id_path: api_cs.Cid_path, collection_def?: api_app.Ccollection__type__property) {
	let key: string | undefined;
	while (id_path.properties.has_steps.state.name === "yes") {
		const has_steps = id_path.properties.has_steps.state.node;
		switch (has_steps.properties.type.state.name) {
			case "group":
				break;
			case "state":
				break;
			case "collection entry": {
				const $ = has_steps.properties.type.state.node;
				if (!collection_def || $.properties.collection.ref.is(collection_def)) {
					key = $.properties.id;
				}
				break;
			}
			default:
				unexpectedState(has_steps.properties.type.state);
		}
		id_path = has_steps.properties.tail;
	}
	return key;
}

function getKeyFromNode(node: api_cs.Cnode, collection_def?: api_app.Ccollection__type__property): string | undefined {
	let current_node: api_cs.Cnode | undefined = node;
	for (let position = node.input.position(); current_node !== undefined; position = current_node.input.position()) {
		switch (position.variant.name) {
			case "entity": {
				const entity = position.variant.definition;
				switch (entity.variant.name) {
					case "root":
						current_node = undefined;
						return;
					case "entry": {
						const entry = entity.variant.definition;
						if (!collection_def || entry.parent.inferences.collection_type().is(collection_def)) {
							current_node = undefined;
							return Entry.getIndex(entry);
						} else {
							current_node = entry.parent.parent.parent.parent;
							break;
						}
					}
					default:
						unexpectedState(entity.variant);
				}
				break;
			}
			case "group":
				current_node = position.variant.definition.parent.parent.parent;
				break;
			case "state":
				current_node = position.variant.definition.parent.parent.parent.parent;
				break;
			case "root":
				current_node = undefined;
				return;
			default:
				unexpectedState(position.variant);
		}
	}
	return undefined;
}

export function getQueryEntryKey(query_entry: api_cs.Cquery_entry_status): string | undefined {
	switch (query_entry.properties.initialized.state.name) {
		case "yes": {
			const entry = query_entry.properties.initialized.state.node.properties.referenced_node.output
				.result_node()
				.input.entity()
				.cast("entry");
			return Entry.getKeyValue(entry);
		}
		case "no": {
			const id_path = query_entry.properties.initialized.state.node.properties.referenced_node;
			return getKeyFromIdPath(id_path);
		}
		case "unknown": {
			const unkown = query_entry.properties.initialized.state.node;
			const entry = getLocalEntry(unkown);
			if (entry === undefined) {
				return undefined;
			} else {
				return Entry.getKeyValue(entry);
			}
		}
		default:
			return unexpectedState(query_entry.properties.initialized.state);
	}
}

export function buildQueryEntryIndex(
	query: api_cs.Cquery__client_state,
	getKeyFunc: (collection_def?: api_app.Ccollection__type__property) => string | undefined
): string | undefined {
	switch (query.properties.use_for.state.name) {
		case "query": {
			let query_path = query.properties.use_for.state.node.inferences.node_context().properties.path;
			let index = "";
			while (query_path.properties.has_steps.state.name === "yes") {
				const $ = query_path.properties.has_steps.state.node;
				index = index.length > 0 ? index + "." : index;
				index += getKeyFunc($.properties.collection.ref.inferences.collection()) ?? "";
				query_path = $.properties.tail;
			}
			return index;
		}
		case "candidates":
			return getKeyFunc();
		default:
			unexpectedState(query.properties.use_for.state);
	}
}

export function getQueryEntryIndex(query_entry: api_cs.Cquery_entry_status): string | undefined {
	const query = query_entry.input.query();
	switch (query_entry.properties.initialized.state.name) {
		case "yes": {
			const initialized = query_entry.properties.initialized.state.node;
			const node = initialized.properties.referenced_node.output.result_node();
			return buildQueryEntryIndex(query, (cdef?: api_app.Ccollection__type__property) => getKeyFromNode(node, cdef));
		}
		case "no": {
			const id_path = query_entry.properties.initialized.state.node.properties.referenced_node;
			return buildQueryEntryIndex(query, (cdef?: api_app.Ccollection__type__property) =>
				getKeyFromIdPath(id_path, cdef)
			);
		}
		case "unknown": {
			const unknown = query_entry.properties.initialized.state.node;
			const entry = getLocalEntry(unknown);
			if (entry) {
				return buildQueryEntryIndex(query, (cdef?: api_app.Ccollection__type__property) =>
					getKeyFromNode(entry.properties.node, cdef)
				);
			}
			return undefined;
		}
		default:
			unexpectedState(query_entry.properties.initialized.state);
	}
}

export function getQueryEntryIndexForEntry(
	query: api_cs.Cquery__client_state,
	entry: api_cs.Centries__collection
): string | undefined {
	return buildQueryEntryIndex(query, (cdef?: api_app.Ccollection__type__property) =>
		getKeyFromNode(entry.properties.node, cdef)
	);
}

export function getIndexedResultEntries(
	has_result: api_cs.Cyes__has_result
): Map<string, api_cs.Centries__yes__has_result> {
	const entries_indexed_on_key = new Map<string, api_cs.Centries__yes__has_result>();
	for (const entry of has_result.properties.entries.values()) {
		const index_key = getQueryEntryIndex(entry.properties.entry_status);
		if (index_key !== undefined) {
			entries_indexed_on_key.set(index_key, entry);
		}
	}
	return entries_indexed_on_key;
}

type PatchedUnkownLocation = {
	__local_entry: api_cs.Centries__collection;
};
export function patchEntryWithLocalEntry(
	unkown_location: Cunknown__initialized,
	entry: api_cs.Centries__collection
): void {
	const patched_location = unkown_location as unknown as PatchedUnkownLocation;
	patched_location.__local_entry = entry;
}

export function getLocalEntry(unkown_location: Cunknown__initialized): api_cs.Centries__collection | undefined {
	const patched_location = unkown_location as unknown as PatchedUnkownLocation;
	return patched_location.__local_entry;
}

type PatchedEntryId = {
	__id_path: IdPathBuilder;
};

export function storeEntryId(entry: api_cs.Cquery_entry_status, id_path_builder: IdPathBuilder) {
	(entry as any as PatchedEntryId).__id_path = id_path_builder;
}

export function getEntryId(entry: api_cs.Cquery_entry_status): IdPathBuilder {
	return (entry as any as PatchedEntryId).__id_path;
}

export function getPropertyForCell(
	entry: api_cs.Centries__collection,
	cell: api_cs.Ctext__type__cells
): api_cs.Ctext__type__property | undefined;
export function getPropertyForCell(
	entry: api_cs.Centries__collection,
	cell: api_cs.Cnumber__type__cells
): api_cs.Cnumber__type__property | undefined;
export function getPropertyForCell(
	entry: api_cs.Centries__collection,
	cell: api_cs.Cfile__type__cells
): api_cs.Cfile__type__property | undefined;
export function getPropertyForCell(
	entry: api_cs.Centries__collection,
	cell: api_cs.Cstate_group__type__cells
): api_cs.Cstate_group__type__property | undefined;
export function getPropertyForCell(
	entry: api_cs.Centries__collection,
	cell:
		| api_cs.Ctext__type__cells
		| api_cs.Cnumber__type__cells
		| api_cs.Cfile__type__cells
		| api_cs.Cstate_group__type__cells
):
	| api_cs.Ctext__type__property
	| api_cs.Cnumber__type__property
	| api_cs.Cfile__type__property
	| api_cs.Cstate_group__type__property
	| undefined {
	const column = cell.inferences.column_type().parent.key.ref;
	const context_node = walkNodePath(column.properties.path, entry.properties.node);
	const column_type = column.properties.type.state;
	switch (column_type.name) {
		case "text":
			return context_node?.properties.attributes
				.get(column_type.node.properties.text.entry)
				?.properties.type.cast("property")
				.properties.type.cast("text");
		case "number": {
			return context_node?.properties.attributes
				.get(column_type.node.properties.number.entry)
				?.properties.type.cast("property")
				.properties.type.cast("number");
			break;
		}
		case "file": {
			return context_node?.properties.attributes
				.get(column_type.node.properties.file.entry)
				?.properties.type.cast("property")
				.properties.type.cast("file");
			break;
		}
		case "state group": {
			return context_node?.properties.attributes
				.get(column_type.node.properties.state_group.entry)
				?.properties.type.cast("property")
				.properties.type.cast("state group");
			break;
		}
		case "widget":
			return undefined;
	}
}

function findCollectionForQuery(query: api_cs.Cquery__client_state, destructor: DestructorTree) {
	switch (query.properties.use_for.state.name) {
		case "query": {
			const for_query = query.properties.use_for.state.node;
			const node = for_query.inferences.node();
			const node_context_query = for_query.inferences.node_context();
			switch (node_context_query.properties.switch_.state.name) {
				case "current":
					switch (node_context_query.properties.path.properties.has_steps.state.name) {
						case "yes": {
							const has_steps = node_context_query.properties.path.properties.has_steps.state.node;
							switch (has_steps.properties.head.properties.has_steps.state.name) {
								case "no":
									switch (has_steps.properties.tail.properties.has_steps.state.name) {
										case "no":
											return registerCollection(
												has_steps.properties.collection.ref.inferences.collection(),
												node,
												destructor
											);
										case "yes": //can't create new entries for sub nodes
									}
									break;
								case "yes":
							}
							break;
						}
						case "no":
					}
					break;
				case "root":
			}
			break;
		}
		case "candidates": // this is determined when the reference is selected.
	}
	return undefined;
}

class QueryInterface extends ClientStateObjectProxy {
	private destructor: DestructorTree;
	readonly entriesAreEditable: Scalar<boolean | Unknown>;
	readonly collection: iCollection | undefined;

	constructor(readonly query: api_cs.Cquery__client_state) {
		super();
		this.destructor = new DestructorTree();
		const collection = findCollectionForQuery(query, this.destructor);
		if (collection) {
			this.collection = iCollection(collection);
			this.entriesAreEditable = createVariable(
				createExpressionBuilder(this.collection.canCreateEntry).pipe((can_create_entry) => {
					if (can_create_entry) {
						query.properties.entries_are_editable.set("yes", {
							"is importing": ["no", {}],
							"can create entry": [
								"yes",
								{
									in: [
										"query",
										{
											collection: collection.parent.parent.key_value,
										},
									],
								},
							],
						});
					} else {
						query.properties.entries_are_editable.set("no", {});
					}
					return can_create_entry;
				})(),
				false
			);
		} else {
			this.entriesAreEditable = Unknown;
		}
		query.onDestroy(() => this.destructor.destroy());
	}
}

export const iQuery = WeakCache<api_cs.Cquery__client_state, QueryInterface>((query) => new QueryInterface(query));
